import { inject, Injectable, signal, computed } from '@angular/core';
import { catchError, forkJoin, map, of, takeUntil, timeout } from 'rxjs';

import { DashboardService, LicenseDetail, LicenseDetailsParams } from '../services/dashboard.service';
import { SearchVocabularyStore } from './search-vocabulary.store';
import { UserService } from '../services/user.service';
import { AppStateResetService } from '../services/app-state-reset.service';
import { buildSuggestions } from '../services/search-suggestions.builder';
import { SuggestionGroup, UserRecordAggregate } from '../models/search-suggestions.model';
import { normalizeSearchText } from '../utils/search-normalize.util';

const STATUTORY_FLAGS = ['S', 'I'] as const;

/**
 * Budget for each `getLicenseDetails` call this store makes. Set to the SAME 60s this repo
 * already budgets for this exact endpoint — `WorkspacePageStore`'s `GRID_TIMEOUT_MS` — rather
 * than a fresh, unrelated number: "`/Lifecycle/get-workspace-license-details` legitimately
 * answers well past 20s on large customers" is a property of the ENDPOINT, not of which store
 * calls it. Not imported directly (`GRID_TIMEOUT_MS` is unexported, module-private to
 * `workspace-page.store.ts`) — declared locally instead, the same way `SearchVocabularyStore`
 * declares its own `LIST_TIMEOUT_MS` rather than sharing one constant across independent
 * stores (see that store's class doc for why the two stores stay independent by design).
 *
 * Without this, a request that never emits and never errors (a genuine hang, not a rejection —
 * both are "observed" for this endpoint) leaves `forkJoin` silent forever: `.subscribe`'s
 * callback — which clears `inFlightFor` — never runs, and the identity is wedged as
 * permanently "in flight," so every later `ensureLoaded()` call is a silent no-op for the rest
 * of the session. Same failure shape `catchError` below guards against, just triggered by a
 * hang instead of an error — `timeout()` converts a hang into an error `catchError` can then
 * degrade, exactly as `SearchVocabularyStore.safeListOutcome` chains the two.
 */
const FETCH_TIMEOUT_MS = 60000;

/**
 * Translates a raw `mgr_status` workflow value onto the wire-value spelling
 * `LICENSE_STATUS_OPTIONS` (`src/app/constants/license-status-options.constants.ts`) — and
 * therefore `SearchVocabularyStore`'s `status` vocabulary entries, which are keyed by that same
 * `value` string — uses.
 *
 * Investigated (Task 5 Step 1) rather than assumed: `license-status-options.constants.ts`'s own
 * doc comment (rule 178) states PendingForReview's `value` "stays unspaced ... the same string
 * the grid returns in `mgr_status`" for every status EXCEPT one — "Application Rejected is the
 * odd one out the OTHER way: the backend filter takes the SPACED 'Application Rejected' ...
 * even though `mgr_status` still reports the workflow state unspaced." Independently
 * corroborated twice more: `date-urgency.util.ts`'s `normalizeStatusKey`/
 * `DEAD_LICENSE_STATUS_KEYS` (called with `mgr_status` from `WorkspacePageStore.toWorkspaceRow`)
 * and `management-dashboard-metrics.ts`'s own `normalizeStatusKey`, both of which exist
 * SPECIFICALLY to fold `'Application Rejected'`/`'ApplicationRejected'` onto one classification
 * key. No other status shows any such split anywhere in the codebase. So only this one entry
 * needs translating; every other `mgr_status` value is used as-is.
 */
const MGR_STATUS_TO_WIRE_NAME: Record<string, string> = {
  ApplicationRejected: 'Application Rejected',
};

function wireStatusName(mgrStatus: string): string {
  return MGR_STATUS_TO_WIRE_NAME[mgrStatus] ?? mgrStatus;
}

function baseParams(customer_id: number, user_id: number, role: string, statutory: 'S' | 'I'): LicenseDetailsParams {
  return {
    customer_id,
    user_id,
    role,
    is_statutory_or_internal: statutory,
    role_flag: null,
    entity_ids: [],
    status_list: [],
    license_type_list: [],
    user_list: [],
    department_list: [],
    start_date: null,
    end_date: null,
    end_date_from: null,
    search_text: '',
    document_status: null,
    license_term: null,
  };
}

function emptyAggregate(): UserRecordAggregate {
  return {
    statusCounts: new Map(),
    locationCounts: new Map(),
    licenseTypeCounts: new Map(),
    departmentCounts: new Map(),
    personNameCounts: new Map(),
  };
}

function bump<K>(map: Map<K, number>, key: K | undefined | null): void {
  if (key == null) return;
  map.set(key, (map.get(key) ?? 0) + 1);
}

/** Outcome of one guarded `getLicenseDetails` call (mirrors `SearchVocabularyStore.ListOutcome`):
    `rows` for aggregation (always an array, even on failure) plus whether the fetch actually
    succeeded — the distinction the fetch-once latch below must not lose, or a transient failure
    gets cached as "loaded" and is never retried. */
interface FetchOutcome {
  ok: boolean;
  rows: LicenseDetail[];
}

function aggregate(rows: LicenseDetail[]): UserRecordAggregate {
  const acc = emptyAggregate();
  for (const r of rows) {
    if (r.mgr_status) bump(acc.statusCounts, wireStatusName(r.mgr_status));
    bump(acc.locationCounts, r.customer_branch_id);
    bump(acc.licenseTypeCounts, r.license_type_id);
    bump(acc.departmentCounts, r.department_id);
    if (r.performer_name) bump(acc.personNameCounts, normalizeSearchText(r.performer_name));
    if (r.reviewer_name) bump(acc.personNameCounts, normalizeSearchText(r.reviewer_name));
  }
  return acc;
}

/**
 * Root-provided, fetch-once-per-identity aggregate of the signed-in user's OWN license records
 * (via the same `get-workspace-license-details` endpoint `WorkspacePageStore` uses for the
 * grid), feeding Task 2's `buildSuggestions` to rank the search suggestions panel's per-group
 * items by how often each value actually appears in the user's own records.
 *
 * Deliberately LAZY, unlike `SearchVocabularyStore` (which `ensureLoaded()`s at app boot):
 * `ensureLoaded()` here is called only from `IntentSearchComponent` on the search box's first
 * focus (Task 7), since not every session opens the search box at all.
 */
@Injectable({ providedIn: 'root' })
export class SearchSuggestionsStore {
  private readonly dashboard = inject(DashboardService);
  private readonly vocabulary = inject(SearchVocabularyStore);
  private readonly user = inject(UserService);
  private readonly appReset = inject(AppStateResetService);

  private readonly _aggregate = signal<UserRecordAggregate>(emptyAggregate());
  readonly groups = computed<SuggestionGroup[]>(() => buildSuggestions(this.vocabulary.entries(), this._aggregate()));

  /** Identity (`customer|user`) the cache holds a FULLY successful result for; `null` until
      one arrives. Latched only when BOTH statutory-flag requests succeeded (see `FetchOutcome`
      / the `subscribe` below) — never on "one arrived", so a failed/timed-out request can't get
      silently absorbed into a "loaded" latch and go permanently missing from suggestions for the
      rest of the session (mirrors `SearchVocabularyStore.loadedFor`). A partial failure still
      populates `_aggregate` with whatever DID load, but leaves `loadedFor` unset so the NEXT
      `ensureLoaded()` call retries both requests. */
  private loadedFor: string | null = null;

  /** Identity whose fetch is currently IN FLIGHT; `null` when none is — needed alongside
      `loadedFor` so a second `ensureLoaded()` call made before the first response arrives
      does not start a duplicate fetch (mirrors `SearchVocabularyStore`). */
  private inFlightFor: string | null = null;

  constructor() {
    this.appReset.register(() => {
      this._aggregate.set(emptyAggregate());
      this.loadedFor = null;
      this.inFlightFor = null;
    });
  }

  private identity(): string {
    return `${this.user.getCustomerId()}|${this.user.getUserId()}`;
  }

  /** Fetch once per identity (cached); a repeat call for the SAME identity — whether the first
      has already arrived or is still in flight — is a no-op. */
  ensureLoaded(): void {
    const identity = this.identity();
    if (this.loadedFor === identity || this.inFlightFor === identity) return;
    this.inFlightFor = identity;

    const customer_id = this.user.getCustomerId();
    const user_id = this.user.getUserId();
    const role = this.user.getRole();

    forkJoin(
      STATUTORY_FLAGS.map((statutory) =>
        this.dashboard.getLicenseDetails(baseParams(customer_id, user_id, role, statutory)).pipe(
          // A hung request (never emits, never errors) must not wedge `forkJoin` silent
          // forever — see `FETCH_TIMEOUT_MS`'s doc. Converts a hang into a `TimeoutError`,
          // which `catchError` below then degrades exactly like any other error.
          timeout(FETCH_TIMEOUT_MS),
          map((r): FetchOutcome => ({ ok: true, rows: r.body?.data ?? r.body?.Data ?? [] })),
          // A request that errors, times out (see above), or the backend returns a non-2xx
          // must degrade to `{ok:false, rows:[]}` instead of erroring the whole `forkJoin` — an
          // uncaught error would skip `next` entirely, leaving `inFlightFor` set forever and
          // wedging every later `ensureLoaded()` call as a permanent no-op (mirrors
          // `SearchVocabularyStore.safeListOutcome`).
          catchError(() => of<FetchOutcome>({ ok: false, rows: [] })),
        ),
      ),
    )
      // Cancels the in-flight fetch on session teardown so a response for the PREVIOUS user
      // can never land after the reset callback above has cleared `_aggregate` and overwrite it
      // with stale data — same reasoning as `SearchVocabularyStore.ensureLoaded`'s own
      // `takeUntil(this.appReset.sessionEnd)`; see `AppStateResetService.sessionEnd`'s doc.
      .pipe(takeUntil(this.appReset.sessionEnd))
      .subscribe((outcomes) => {
        this.inFlightFor = null;
        this._aggregate.set(aggregate(outcomes.flatMap((o) => o.rows)));
        // Latch only when BOTH requests fully succeeded — see `loadedFor`'s doc.
        if (outcomes.every((o) => o.ok)) {
          this.loadedFor = identity;
        }
      });
  }
}
